import { formatInTimeZone } from 'date-fns-tz';

import { DateFormat } from '../../enums/dateFormat';
import { getLocalTimeZone } from './getLocalTimeZone';

interface Options {
  dateTime: Date | string;
  timeZone?: string;
  dateFormat?: DateFormat;
}

/**
 * Takes a timestamp, timezone and format and returns the formatted date as a string.
 * @param dateTime
 * @param timeZone
 * @param dateFormat, optional, defaults to `DateFormat.default`
 * @returns string date
 * @example
 * ```
 * formatDateTime({
 *    dateTime: '2021-10-10T05:00:00.000Z',
 *    timeZone: 'America/New_York'
 * }) //=> 10 Oct 2021
 * formatDateTime({
 *    dateTime: new Date(2021, 9, 9, 22),
 *    timeZone: 'America/New_York',
 *    dateFormat: DateFormat.monthDayYearHourMinuteAbbreviatedTimezone
 * }) //=> 10 Oct 2021 01:00 EDT
 * ```
 */
export const formatDateTime = (options: Options): string => {
  try {
    const { dateTime, timeZone, dateFormat } = options;
    const date = new Date(dateTime);
    const outputFormat = dateFormat ?? DateFormat.default;

    const formatted = formatInTimeZone(
      date,
      timeZone || getLocalTimeZone(),
      outputFormat,
    );

    // `formatToTimeZone` returns 'Invalid date' if invalid, we want to return a string in all cases
    return formatted.toLowerCase().indexOf('invalid') === -1 ? formatted : '';
  } catch (e) {
    // Necessary because `formatToTimeZone` calls `findTimeZone` in the `timezone-support` package and that will
    // throw an error `Unknown time zone "${name}".` if it gets a "bad" timezone. For us it's just a no-op.
    return '';
  }
};
